import { Action, PayloadAction } from "@reduxjs/toolkit";
import translations from "constants/translations.json";

// State definition
export type OptionsState = {
  options: {
    [OptionKey.HideSensitiveData]: ToggleItem;
    [OptionKey.ShowFolderHierarchy]: ToggleItem;
    [OptionKey.ElementSize]: SelectorItem;
    [OptionKey.Grouping]: ToggleItem;
    [OptionKey.MultipleViews]: ToggleItem;
    [OptionKey.ShowImages]: ToggleItem;
    [OptionKey.ColorScheme]: SelectorItem;
    [OptionKey.LinearLayout]: ToggleItem;
    [OptionKey.ColumnCount]: NumberItem;
  };
  currentVis: VisTypes;
  navSidebarOpen: boolean;
  configSidebarOpen: boolean;
  searchString: string;
  // version of optionstate, increase to delete older stores that are saved in persistent state
  version: number;
};

export function isOptionsState(obj: unknown): obj is OptionsState {
  return (
    obj !== null &&
    (obj as OptionsState).options &&
    typeof (obj as OptionsState).navSidebarOpen === "boolean" &&
    typeof (obj as OptionsState).configSidebarOpen === "boolean" &&
    typeof (obj as OptionsState).searchString === "string" &&
    typeof (obj as OptionsState).version === "number"
  );
}

export enum VisTypes {
  All = "All",
  Provider = "Provider",
  Files = "Files",
  Timeline = "Timeline",
  Sparklines = "Sparklines",
  Piechart = "Piechart",
  List = "List",
  Grid = "Grid",
  Sankey = "Sankey",
  Sunburst = "Sunburst",
  Export = "Export",
  ContactGraph = "ContactGraph",
  LocationHistory = "LocationHistory",
  Barchart = "Barchart",
}

export enum OptionKey {
  HideSensitiveData = "HideSensitiveData",
  ShowFolderHierarchy = "ShowFolderHierarchy",
  ElementSize = "ElementSize",
  Grouping = "Grouping",
  MultipleViews = "MultipleViews",
  ShowImages = "ShowImages",
  ColorScheme = "ColorScheme",
  LinearLayout = "LinearLayout",
  ColumnCount = "ColumnCount",
}

// Action types definition
export enum OptionsActionTypes {
  SWITCH_VIS = "@@options/SWITCH_VIS",
  SELECT = "@@options/SELECT",
  TOGGLE = "@@options/TOGGLE",
  SEARCH = "@@options/SEARCH",
  SIDEBAR = "@@options/SIDEBAR",
  CONFIG = "@@options/CONFIG",
}

type BaseItem = {
  value: string | boolean | number;
  view: VisTypes[];
  type: ItemType;
};

export type SelectorItemTextType = keyof typeof translations.en.Options.SelectorItemOptions;

export enum ItemType {
  toggleItem = "ToggleItem",
  selectorItem = "SelectorItem",
  rangeItem = "RangeItem",
  sliderItem = "SliderItem",
  lassoItem = "LassoItem",
  textItem = "TextItem",
  numberItem = "NumberItem",
}

export type ToggleItem = BaseItem & {
  type: ItemType.toggleItem;
  value: boolean;
};
export type SelectorItem = BaseItem & {
  type: ItemType.selectorItem;
  value: string;
  options: SelectorItemTextType[];
};
export type RangeItem = BaseItem & {
  type: ItemType.rangeItem;
  value: number;
  start: number;
  end: number;
};
export type SliderItem = BaseItem & {
  type: ItemType.sliderItem;
  value: number;
  step: number;
  start: number;
  end: number;
};
export type LassoItem = BaseItem & {
  type: ItemType.lassoItem;
  value: boolean;
  amount: number;
  items: number[];
};
export type TextItem = BaseItem & {
  type: ItemType.textItem;
  value: string;
};
export type NumberItem = BaseItem & {
  type: ItemType.numberItem;
  value: number;
};

export type Item = SelectorItem | RangeItem | LassoItem | ToggleItem | SliderItem | TextItem | NumberItem;

type SwitchVisAction = PayloadAction<{ label: VisTypes }, OptionsActionTypes.SWITCH_VIS>;
type SelectAction = PayloadAction<{ id: OptionKey; value: string | boolean | number }, OptionsActionTypes.SELECT>;
type ToggleAction = Action<OptionsActionTypes.TOGGLE>;
type SearchAction = PayloadAction<{ label: string }, OptionsActionTypes.SEARCH>;
type SidebarAction = PayloadAction<{ label: boolean | undefined }, OptionsActionTypes.SIDEBAR>;
type ConfigSidebarAction = PayloadAction<{ label: boolean | undefined }, OptionsActionTypes.CONFIG>;

export type OptionActions =
  | SwitchVisAction
  | SelectAction
  | ToggleAction
  | SearchAction
  | SidebarAction
  | ConfigSidebarAction;
